import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Heart, ShoppingBag, GraduationCap, Building2, Plane, Bitcoin } from 'lucide-react';

export const Industries = () => {
  const industries = [
    { icon: Heart, title: 'Healthcare & Medical', description: 'HIPAA-compliant solutions for healthcare providers, telemedicine platforms, patient management systems, and medical device integration.', solutions: ['Electronic Health Records', 'Telemedicine Apps', 'Patient Portals', 'Medical Device Integration'] },
    { icon: ShoppingBag, title: 'E-commerce & Retail', description: 'Scalable e-commerce platforms, inventory management, payment gateways, and omnichannel retail solutions.', solutions: ['Custom E-commerce Platforms', 'POS Systems', 'Inventory Management', 'Customer Analytics'] },
    { icon: Bitcoin, title: 'FinTech & Banking', description: 'Secure financial applications, payment processing, blockchain solutions, and compliance-ready banking platforms.', solutions: ['Mobile Banking Apps', 'Payment Gateways', 'Blockchain Solutions', 'Trading Platforms'] },
    { icon: GraduationCap, title: 'Education & E-learning', description: 'Learning management systems, virtual classrooms, student portals, and interactive educational content platforms.', solutions: ['LMS Platforms', 'Virtual Classrooms', 'Student Management', 'Assessment Tools'] },
    { icon: Plane, title: 'Travel & Hospitality', description: 'Booking platforms, property management systems, guest experience apps, and travel aggregation solutions.', solutions: ['Booking Engines', 'Property Management', 'Guest Apps', 'Travel Planning Tools'] },
    { icon: Building2, title: 'Real Estate & PropTech', description: 'Property listings, virtual tours, CRM systems, and smart building management solutions.', solutions: ['Property Portals', 'CRM Systems', 'Virtual Tours', 'Smart Building Solutions'] }
  ];

  const stats = [
    { value: '25+', label: 'Industries Served' },
    { value: '500+', label: 'Projects Delivered' },
    { value: '150+', label: 'Global Clients' },
    { value: '98%', label: 'Success Rate' }
  ];

  return (
    <>
      {/* Meta Tags for SEO */}
      <Helmet>
        <title>Industries | Novaseeds Technologies</title>
        <meta name="description" content="Explore Novaseeds Technologies' industry expertise across Healthcare, FinTech, E-commerce, Education, Travel, and Real Estate. Innovative solutions tailored to your sector." />
      </Helmet>

      <div className="min-h-screen pt-32 pb-20 bg-background">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">

          {/* Hero */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-20">
            <span className="text-sm uppercase tracking-widest text-muted-foreground font-bold">INDUSTRIES</span>
            <h1 className="text-5xl md:text-7xl font-bold tracking-tight leading-none mt-4 mb-6">
              <span className="text-foreground">Industry Expertise</span><br />
              <span className="text-gradient">Across Sectors</span>
            </h1>
            <p className="text-base md:text-lg leading-relaxed text-muted-foreground max-w-3xl mx-auto">
              We bring deep domain knowledge and technical expertise to solve complex challenges across diverse industries
            </p>
          </motion.div>

          {/* Industries Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-20">
            {industries.map((industry, index) => {
              const Icon = industry.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-card border border-[#E2E8F0] shadow-[0_2px_8px_rgba(0,0,0,0.05)] p-8 rounded-3xl hover:shadow-lg transition-all hover:-translate-y-1 group"
                >
                  <div className="flex items-start space-x-4 mb-6">
                    <div className="w-14 h-14 rounded-2xl bg-blue-50 border border-blue-200 flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                      <Icon size={28} className="text-blue-600" />
                    </div>
                    <div>
                      <h3 className="text-2xl font-semibold text-foreground mb-2">{industry.title}</h3>
                    </div>
                  </div>

                  <p className="text-muted-foreground mb-6 leading-relaxed">{industry.description}</p>

                  <div>
                    <h4 className="text-sm font-semibold text-muted-foreground mb-3 uppercase tracking-wider">Our Solutions:</h4>
                    <div className="grid grid-cols-2 gap-3">
                      {industry.solutions.map((solution, idx) => (
                        <div key={idx} className="flex items-start space-x-2">
                          <span className="text-blue-600 mt-1">•</span>
                          <span className="text-muted-foreground text-sm">{solution}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>

          {/* Stats */}
          <motion.section initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="mb-20 bg-muted/50 border border-[#E2E8F0] rounded-3xl p-12">
            <h2 className="text-3xl md:text-4xl font-semibold text-center text-foreground mb-12">Our Industry Impact</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
              {stats.map((stat, index) => (
                <div key={index} className="text-center">
                  <div className="text-4xl md:text-5xl font-bold text-gradient mb-2">{stat.value}</div>
                  <div className="text-muted-foreground text-sm">{stat.label}</div>
                </div>
              ))}
            </div>
          </motion.section>

          {/* CTA */}
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="bg-blue-50 border border-blue-200 rounded-3xl p-12 text-center">
            <h2 className="text-4xl md:text-5xl font-semibold tracking-tight mb-6">
              <span className="text-foreground">Let's Transform Your Industry</span>
            </h2>
            <p className="text-base md:text-lg leading-relaxed text-muted-foreground max-w-2xl mx-auto mb-8">
              Partner with us to build innovative solutions tailored to your industry needs
            </p>
            <Link to="/contact" className="inline-block bg-blue-600 hover:bg-blue-700 text-white rounded-full px-8 py-3 font-semibold shadow-[0_2px_8px_rgba(37,99,235,0.25)] transition-all hover:scale-105 active:scale-95">
              Start Your Project
            </Link>
          </motion.div>

        </div>
      </div>
    </>
  );
};
